// src/store/navigationStore.js
// Zustand tabanlı Navigation + Modal store — Adım 6
// App.js'teki activePage, modalContent, modalData, profileRouteUsername
// state'lerini merkezi yönetir.
// Diğer bileşenler import ederek doğrudan erişebilir — prop drilling gereksiz.
import { create } from "zustand";

/**
 * Modal yükü — ÜÇ YAZAN TARAFIN HEPSİ ölçülerek türetildi (uydurulmadı):
 *   · `null`            → App.tsx:572/588/595/604/653/792/842, appRouting ×9
 *   · `undefined`       → App.tsx:802 `setModalData(userId)` — `userId?: string`
 *                         (bunu GEVŞEK tsc değil, STRICT küme yakaladı)
 *   · düz `string` (uid)→ App.tsx:373 `setModalData(String(userId))`, :804
 *   · nesne             → post/clip/place (App.tsx:718/749/786) ve
 *                         `{placeId}` / `{matchId}` / `{conversationId}` /
 *                         `{derinDurum}` / `{kamera:true}` (appRouting)
 *
 * 📜 NİYE ÖNCEDEN `unknown`DI: bu alanın tipi `appRouting`de
 * `Record<string, unknown> | null` yazıyordu — `string` dalını DIŞARIDA
 * bırakan bir daralma. Store o yalanı kopyalamak yerine `unknown` demeyi
 * seçmişti (dürüst ama bilgisiz). Sonuç: `App.tsx`te üç ayrı `as ModalVerisi`
 * köprüsü ve her birinin yanında "sözleşme bu noktada YALAN, ayrı tur"
 * yorumu. Bu, o ayrı turdur: daralma yerine GERÇEK yazıldı, `unknown` da
 * gereksiz kaldı.
 *
 * 🧭 Ders: bir tip gövdeyi yalanlıyorsa iki sonuç doğurur — ya `as` ile
 * susturulur (görünmezlik borcu), ya `unknown`a kaçılır (bilgi kaybı).
 * İkisi de tipin kendisini düzeltmenin yerine geçmez.
 */
export type ModalVerisi = string | Record<string, unknown> | null | undefined;

/**
 * Store'un ŞEKLİ GÖVDEDEN okundu: dört state alanı + yedi aksiyon.
 */
export type GezinmeDurumu = {
  activePage: string;
  profileRouteUsername: string | null;
  modalContent: string | null;
  /** Şekil yukarıda ölçüldü — artık `unknown` değil, GERÇEK birleşim. */
  modalData: ModalVerisi;
  setActivePage: (page: string) => void;
  setProfileRouteUsername: (username: string | null) => void;
  setModalContent: (content: string | null) => void;
  setModalData: (data: ModalVerisi) => void;
  openModal: (content: string | null, data?: ModalVerisi) => void;
  closeModal: () => void;
  navigateTo: (page: string, username?: string | null) => void;
};

export const useNavigationStore = create<GezinmeDurumu>((set, get) => ({
  // ─── State ───────────────────────────────────────────────────────────────
  activePage: "home",
  profileRouteUsername: null,
  modalContent: null,
  modalData: null,

  // ─── Page Actions ─────────────────────────────────────────────────────────
  setActivePage: (page) => set({ activePage: page }),
  setProfileRouteUsername: (username) => set({ profileRouteUsername: username }),

  // ─── Modal Actions ────────────────────────────────────────────────────────
  setModalContent: (content) => set({ modalContent: content }),
  setModalData: (data) => set({ modalData: data }),

  openModal: (content, data = null) =>
    set({ modalContent: content, modalData: data }),

  closeModal: () =>
    set({ modalContent: null, modalData: null }),

  // ─── Combined Navigation ──────────────────────────────────────────────────
  navigateTo: (page, username = null) =>
    set({
      activePage: page,
      profileRouteUsername: username,
      modalContent: null,
      modalData: null,
    }),
}));

// Selector'lar — gereksiz re-render'ı önler
export const selectActivePage = (s: GezinmeDurumu) => s.activePage;
export const selectProfileRouteUsername = (s: GezinmeDurumu) => s.profileRouteUsername;
export const selectModalContent = (s: GezinmeDurumu) => s.modalContent;
export const selectModalData = (s: GezinmeDurumu) => s.modalData;
export const selectSetActivePage = (s: GezinmeDurumu) => s.setActivePage;
export const selectSetProfileRouteUsername = (s: GezinmeDurumu) => s.setProfileRouteUsername;
export const selectSetModalContent = (s: GezinmeDurumu) => s.setModalContent;
export const selectSetModalData = (s: GezinmeDurumu) => s.setModalData;
export const selectOpenModal = (s: GezinmeDurumu) => s.openModal;
export const selectCloseModal = (s: GezinmeDurumu) => s.closeModal;
export const selectNavigateTo = (s: GezinmeDurumu) => s.navigateTo;
